import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { ModalOverlay, SkeletonBar } from 'components/v3';
import { fieldErrorsOf } from 'shared/apolloFieldErrors';
import {
  useFormFileDownloadUrl,
  useGetFormSubmission,
  useUpdateFormSubmissionAdmin,
} from '../../gql';
import Glyph from '../Glyph';
import { sx } from '../sx';
import EntryCard from './EntryCard';
import { downloadEntryExcel, printEntry } from './entryDownloads';

// One entry in a centred modal (design EntryModal, Overview layout) — it
// replaced the right-hand drawer. Loads the full entry (answers, its
// version, files), saves staff edits through updateFormSubmissionAdmin and
// runs the single-entry downloads. Full screen on phones. While an edit is
// open the backdrop doesn't close it, and Esc steps back one level first
// (menu → field → clean edit) before closing.
//
// Optional W-9 props (w9-plan T15, §6.3 item 9), each off by default so a
// regular form's modal is unchanged:
//   extraActions(submission) | node     the card's extra action buttons
//   extraSections(submission) | node    sections after the answers
//   editableFieldIds                    the only editable fields
//   downloadItems(submission) | array   extra Download menu rows
//   revealSecret(submissionId, fieldId) → Promise<{ data: text } | { error }>
//       the TIN reveal (T11): the full number shows for REVEAL_MS, a toast
//       says it was recorded, then it masks again.

const Placeholder = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 28px;
  color: ${(p) => sx(p.theme).text2};
`;

const NoticeTitle = styled.div`
  font-weight: 700;
  color: ${(p) => sx(p.theme).text};
`;

const CloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CloseX = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  background: transparent;
  color: ${(p) => sx(p.theme).text2};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const panelStyle = (theme, isMobile) => {
  const x = sx(theme);
  return isMobile
    ? { width: '100%', height: '100%', display: 'flex', background: x.card }
    : {
        width: 'min(1280px, 100%)',
        height: 'min(1000px, 100%)',
        display: 'flex',
        background: x.card,
        border: `1px solid ${x.line2}`,
        borderRadius: 22,
        boxShadow: x.pop,
        overflow: 'hidden',
      };
};

export const REVEAL_MS = 30000;
export const REVEAL_TOAST = 'Full TIN shown · recorded in the access log';

const resolveSlot = (slot, submission) =>
  typeof slot === 'function' ? slot(submission) : slot;

// The reveal state: { revealed: { [fieldId]: text }, revealingId }, with a
// timer per field that masks it again after REVEAL_MS. A new entry drops
// everything.
const useReveal = (revealSecret, submission, showToast, mounted) => {
  const [revealed, setRevealed] = useState({});
  const [revealingId, setRevealingId] = useState(null);
  const timers = useRef({});
  const clearTimers = useCallback(() => {
    Object.values(timers.current).forEach((t) => clearTimeout(t));
    timers.current = {};
  }, []);
  const entryId = submission ? submission.id : null;
  useEffect(() => {
    clearTimers();
    setRevealed({});
    setRevealingId(null);
  }, [entryId, clearTimers]);
  useEffect(() => () => clearTimers(), [clearTimers]);

  const reveal = useCallback(
    async (fieldId) => {
      if (!revealSecret || !submission) return;
      setRevealingId(fieldId);
      const res = await revealSecret(submission.id, fieldId);
      if (!mounted.current) return;
      setRevealingId(null);
      if (!res || res.error) {
        showToast({
          type: 'error',
          title: (res && res.error && res.error.global) || "Couldn't reveal",
        });
        return;
      }
      setRevealed((cur) => ({ ...cur, [fieldId]: res.data }));
      showToast({ type: 'info', title: REVEAL_TOAST });
      if (timers.current[fieldId]) clearTimeout(timers.current[fieldId]);
      timers.current[fieldId] = setTimeout(() => {
        delete timers.current[fieldId];
        if (!mounted.current) return;
        setRevealed((cur) => {
          const next = { ...cur };
          delete next[fieldId];
          return next;
        });
      }, REVEAL_MS);
    },
    [revealSecret, submission, showToast, mounted]
  );

  return { revealed, revealingId, reveal: revealSecret ? reveal : undefined };
};

const EntryModal = ({
  entryId,
  preview,
  form,
  nav,
  isMobile,
  onClose,
  showToast,
  extraActions,
  extraSections,
  editableFieldIds,
  downloadItems,
  revealSecret,
}) => {
  const theme = useTheme();
  const open = !!entryId;
  const { submission, loading, error } = useGetFormSubmission(entryId, {
    skip: !open,
  });
  const update = useUpdateFormSubmissionAdmin();
  const getDownloadUrl = useFormFileDownloadUrl();
  const [busy, setBusy] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const escapeRef = useRef(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const registerEscape = useCallback((fn) => {
    escapeRef.current = fn;
  }, []);
  const { revealed, revealingId, reveal } = useReveal(
    revealSecret,
    submission,
    showToast,
    mounted
  );

  const handleClose = () => {
    if (escapeRef.current && escapeRef.current()) return;
    onClose();
  };

  const definition =
    submission && submission.version ? submission.version.definition : null;
  const formName = (form && form.name) || 'Form';
  const account = form ? form.account : null;

  const save = (answers) => update({ id: submission.id, answers });

  const onPdf = () => {
    const result = printEntry({
      account,
      formName,
      entry: submission,
      definition,
    });
    if (result === 'blocked') {
      showToast({ type: 'error', title: 'Allow pop-ups to open the PDF' });
    }
  };

  const onExcel = () => {
    showToast({ type: 'info', title: 'Building Excel…' });
    return downloadEntryExcel({
      account,
      formName,
      entry: submission,
      definition,
    }).then(
      () =>
        showToast({
          type: 'success',
          title: `${submission.reference} downloaded as Excel`,
        }),
      () => showToast({ type: 'error', title: 'Excel download failed' })
    );
  };

  // The file's link is signed as an attachment, so going to it saves the
  // file and the page stays (window.open after an await is a blocked
  // pop-up in Safari).
  const downloadFile = async (fileId) => {
    setDownloadingId(fileId);
    const res = await getDownloadUrl(fileId);
    if (!mounted.current) return;
    setDownloadingId(null);
    if (res.error) {
      showToast({ type: 'error', title: res.error.global });
      return;
    }
    window.location.assign(res.url);
  };

  let body;
  if (submission) {
    body = (
      <EntryCard
        entry={submission}
        definition={definition}
        nav={nav}
        isMobile={isMobile}
        onClose={onClose}
        onSave={save}
        onPdf={onPdf}
        onExcel={onExcel}
        onDownloadFile={downloadFile}
        downloadingId={downloadingId}
        showToast={showToast}
        registerEscape={registerEscape}
        onEditingChange={setBusy}
        extraActions={resolveSlot(extraActions, submission)}
        extraSections={resolveSlot(extraSections, submission)}
        editableFieldIds={editableFieldIds}
        downloadItems={resolveSlot(downloadItems, submission)}
        onReveal={reveal}
        revealed={revealed}
        revealingId={revealingId}
      />
    );
  } else {
    const failed = error && !loading;
    body = (
      <Placeholder aria-busy={failed ? undefined : 'true'}>
        <CloseRow>
          <CloseX type="button" aria-label="Close" onClick={onClose}>
            <Glyph name="x" size={18} />
          </CloseX>
        </CloseRow>
        {failed ? (
          <div role="alert">
            <NoticeTitle>This entry couldn&apos;t be loaded</NoticeTitle>
            <div style={{ marginTop: 6 }}>{fieldErrorsOf(error).global}</div>
          </div>
        ) : (
          <>
            <NoticeTitle>
              {preview ? `Entry ${preview.reference}` : 'Loading entry…'}
            </NoticeTitle>
            <SkeletonBar $w="45%" $h="26px" />
            {[0, 1, 2, 3].map((i) => (
              <SkeletonBar key={i} $w={i % 2 ? '70%' : '90%'} $h="14px" />
            ))}
          </>
        )}
      </Placeholder>
    );
  }

  return (
    <ModalOverlay
      open={open}
      onClose={handleClose}
      label={
        submission || preview
          ? `Entry ${(submission || preview).reference}`
          : 'Entry'
      }
      placement={isMobile ? 'full' : 'center'}
      closeOnBackdrop={!busy}
      backdrop={sx(theme).overlay}
      zIndex={1100}
      panelStyle={panelStyle(theme, isMobile)}
    >
      {body}
    </ModalOverlay>
  );
};

EntryModal.propTypes = {
  entryId: PropTypes.number,
  // The table row, named in the header while the full entry loads.
  preview: PropTypes.shape({ reference: PropTypes.string }),
  form: PropTypes.shape({
    name: PropTypes.string,
    account: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      logo: PropTypes.string,
    }),
  }),
  nav: PropTypes.object,
  isMobile: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  // W-9 slots (w9-plan T15); all optional.
  extraActions: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  extraSections: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  editableFieldIds: PropTypes.arrayOf(PropTypes.string),
  downloadItems: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  revealSecret: PropTypes.func,
};

EntryModal.defaultProps = {
  entryId: null,
  preview: null,
  form: null,
  nav: null,
  isMobile: false,
  extraActions: null,
  extraSections: null,
  editableFieldIds: null,
  downloadItems: undefined,
  revealSecret: undefined,
};

export default EntryModal;
